import React, { useEffect, useState, useRef } from 'react';
import { adminApi } from '../context/AdminContext';
import { IMAGE_BASE_URL } from '../constant';

const emptyForm = { name: '', slug: '', image: null };

export default function AdminProducts() {
  const [products, setProducts]   = useState([]);
  const [form, setForm]           = useState(emptyForm);
  const [editId, setEditId]       = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [preview, setPreview]     = useState(null);
  const [loading, setLoading]     = useState(false);
  const [fetching, setFetching]   = useState(true);
  const [alert, setAlert]         = useState(null);
  const fileRef = useRef();

  const showAlert = (msg, type = 'success') => {
    setAlert({ msg, type });
    setTimeout(() => setAlert(null), 3500);
  };

  const fetchProducts = () => {
    setFetching(true);
    adminApi.get('/admin/products')
      .then(res => setProducts(res.data))
      .catch(() => showAlert('Failed to load products', 'error'))
      .finally(() => setFetching(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchProducts(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'name' && !editId) {
        updated.slug = value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      }
      return updated;
    });
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm(prev => ({ ...prev, image: file }));
    setPreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setPreview(null);
    setExistingImage(null);
    setForm(prev => ({ ...prev, image: null }));
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return showAlert('Product name is required', 'error');
    setLoading(true);

    const data = new FormData();
    data.append('name', form.name);
    data.append('slug', form.slug);
    if (form.image) data.append('image', form.image);
    if (editId && existingImage) data.append('existing_image', existingImage);

    try {
      if (editId) {
        await adminApi.put(`/admin/products/${editId}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        showAlert('Product updated successfully!');
      } else {
        await adminApi.post('/admin/products', data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        showAlert('Product added successfully!');
      }
      setForm(emptyForm);
      setEditId(null);
      setPreview(null);
      setExistingImage(null);
      if (fileRef.current) fileRef.current.value = '';
      fetchProducts();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Operation failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditId(product.id);
    setForm({ name: product.name || product.slug, slug: product.slug, image: null });
    setExistingImage(product.image_url || null);
    setPreview(product.image_url ? `${IMAGE_BASE_URL}${product.image_url}` : null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This will hide it from the website.`)) return;
    try {
      await adminApi.delete(`/admin/products/${id}`);
      showAlert('Product deleted.');
      fetchProducts();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Delete failed', 'error');
    }
  };

  const handleCancel = () => {
    setForm(emptyForm);
    setEditId(null);
    setPreview(null);
    setExistingImage(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="ap-page">
      {/* ── Clean Header ── */}
      <div className="ap-header">
        <h1 className="ap-title">Manage Products</h1>
        <p className="ap-subtitle">Add and manage your data</p>
      </div>

      {alert && <div className={`alert alert-${alert.type}`}>{alert.msg}</div>}

      {/* ── Form Card ── */}
      <div className="admin-card">
        <div className="admin-card-header">
          <div className="admin-card-title">
            {editId ? '✏️ Edit Product' : '➕ Add New Product'}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-row">
            <div className="form-group">
              <label>Product Name *</label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="e.g. Tomato Seeds"
                required
              />
            </div>
            <div className="form-group">
              <label>Slug (URL)</label>
              <input
                name="slug"
                value={form.slug}
                onChange={handleChange}
                placeholder="e.g. tomato-seeds"
              />
            </div>
          </div>

          {/* Image Upload */}
          <div className="form-group">
            <label>Product Image</label>
            <div className="ap-upload-area">
              <input
                type="file"
                accept="image/jpg,image/jpeg,image/png,image/webp"
                onChange={handleFile}
                ref={fileRef}
                className="ap-file-input"
                id="product-image-input"
              />
              <label htmlFor="product-image-input" className="ap-upload-label">
                <span className="ap-upload-icon">📷</span>
                <span>Click to upload image</span>
                <span className="ap-upload-hint">JPG, PNG, WEBP supported</span>
              </label>
            </div>

            {preview && (
              <div className="ap-preview-wrap">
                <img src={preview} alt="Preview" className="ap-preview-img" />
                <button type="button" className="ap-remove-img" onClick={removeImage}>
                  ✕ Remove
                </button>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? '⏳ Saving...' : editId ? '✅ Update Product' : '➕ Add Product'}
            </button>
            {editId && (
              <button type="button" className="btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* ── Products Table ── */}
      <div className="admin-card">
        <div className="admin-card-header">
          <div className="admin-card-title">📋 All Products ({products.length})</div>
        </div>

        {fetching ? (
          <div className="ap-state">Loading products...</div>
        ) : products.length === 0 ? (
          <div className="ap-state">No products yet. Add your first product above.</div>
        ) : (
          <div className="ap-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Image</th>
                  <th>Name / Slug</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, i) => (
                  <tr key={p.id}>
                    <td>{i + 1}</td>
                    <td>
                      {p.image_url ? (
                        <img
                          src={`${IMAGE_BASE_URL}${p.image_url}`}
                          alt={p.name}
                          className="ap-thumb"
                        />
                      ) : (
                        <div className="ap-no-img">🌱</div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{p.name || p.slug}</div>
                      <div style={{ fontSize: '0.8rem', color: '#94a3b8' }}>{p.slug}</div>
                    </td>
                    <td>
                      <div className="actions">
                        <button className="btn-edit"   onClick={() => handleEdit(p)}>✏️ Edit</button>
                        <button className="btn-danger" onClick={() => handleDelete(p.id, p.name || p.slug)}>🗑️ Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .ap-page { max-width: 900px; }

        /* Clean header */
        .ap-header { margin-bottom: 24px; }
        .ap-title  { font-size: 1.6rem; font-weight: 800; color: #1e293b; margin: 0 0 4px 0; }
        .ap-subtitle { color: #64748b; margin: 0; font-size: 0.95rem; }

        /* Upload area */
        .ap-upload-area { position: relative; }
        .ap-file-input  { position: absolute; width: 1px; height: 1px; opacity: 0; }
        .ap-upload-label {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 6px; padding: 28px 20px;
          border: 2px dashed #cbd5e1; border-radius: 12px;
          background: #f8fafc; cursor: pointer;
          transition: all 0.2s; text-align: center;
          color: #64748b; font-size: 0.9rem;
        }
        .ap-upload-label:hover { border-color: #667eea; background: #f5f3ff; color: #667eea; }
        .ap-upload-icon { font-size: 1.8rem; }
        .ap-upload-hint { font-size: 0.78rem; color: #94a3b8; }

        /* Preview */
        .ap-preview-wrap {
          display: flex; align-items: center; gap: 14px;
          margin-top: 12px; padding: 12px;
          background: #f8fafc; border-radius: 10px;
          border: 1px solid #e2e8f0;
        }
        .ap-preview-img {
          width: 100px; height: 75px; object-fit: cover;
          border-radius: 8px; border: 2px solid #e2e8f0;
        }
        .ap-remove-img {
          padding: 6px 14px; background: #fee2e2; color: #dc2626;
          border: none; border-radius: 8px; cursor: pointer;
          font-size: 0.85rem; font-weight: 600; transition: background 0.2s;
        }
        .ap-remove-img:hover { background: #fecaca; }

        /* Table */
        .ap-table-wrap { overflow-x: auto; }
        .ap-thumb {
          width: 56px; height: 42px; object-fit: cover;
          border-radius: 8px; border: 1px solid #e2e8f0;
        }
        .ap-no-img {
          width: 56px; height: 42px; background: #f1f5f9;
          border-radius: 8px; display: flex; align-items: center;
          justify-content: center; font-size: 1.3rem;
        }
        .ap-state { text-align: center; padding: 40px; color: #94a3b8; font-size: 0.95rem; }
      `}</style>
    </div>
  );
}
